// src/components/clinical/CreateVisitModal.jsx
// Single modal that creates an encounter + clinical note atomically.
// Single-step: form fields + inline template selection on one screen.

import { useState, useEffect, useRef } from 'react';
import { X, Loader2, ChevronDown, ChevronUp, Calendar, Link2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { NoteTemplateSelector } from '@/components/clinical/NoteTemplateSelector';
import { ClinicalRegionPicker } from '@/components/clinical/ClinicalRegionPicker';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { isRehabSpecialty } from '@/utils/specialtyHelpers';
import { computeCaseNoteConstraints } from '@/utils/caseNoteConstraints';
import { timestampToDateOnly } from '@/utils/dateHelpers';
import { useProviderResolution } from '@/hooks/useProviderResolution';
import { useCredentialingDeny } from '@/hooks/useCredentialingDeny';
import { AssignedSignerSelect } from '@/components/clinical/AssignedSignerSelect';
import { getBlockedNoteTypes, getDraftOnlyNoteTypes, canCreateNotes, normalizePlanType } from '@prism-ehr/shared/constants';
import { usesCasesForSpecialty } from '@prism-ehr/shared/utils/caseUtils';
import { resolveCaseBillingCoverage } from '@prism-ehr/shared/utils/caseBillingCoverage';
import { useCaseBillingAlerts } from '@/hooks/useCaseBillingAlerts';
import { derivePnDue } from '@/utils/pnDue';
import { cn } from '@/lib/utils';
import { isTestPatient, resolveTemplateDraft } from '@/utils/templateTesting';
import { linkInvoice } from '@/utils/visitCreation';

const REQUIRED_ACTION_TO_NOTE_TYPES = {
  progress_note: ['progress_note', 'reassessment'],
  re_evaluation: ['reassessment'],
  poc_renewal: ['reassessment'],
  new_evaluation: ['initial_evaluation'],
  authorization_check: [], // intentionally imposes no note-type restriction
};

// Labels for settings.documentation.pn_due_forced_note_types values (array, OR logic)
const FORCED_NOTE_TYPE_LABELS = {
  progress_note: 'Progress Note',
  reassessment: 'Reassessment',
  discharge_summary: 'Discharge Summary',
};

// Appointment statuses eligible for same-day auto-link (WS-D); terminal states are excluded
const AUTO_LINK_STATUSES = ['scheduled', 'confirmed', 'checked_in', 'in_room', 'in_progress'];

function formatAppointmentTime(appt) {
  if (appt?.start_timestamp) {
    return new Date(appt.start_timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (appt?.start_time) {
    const [h, m] = String(appt.start_time).split(':').map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  }
  return '';
}

const TEMPLATE_RESTRICTIONS_BY_ORG = {
  '3c902571-f05a-4036-aa48-881d16480b0d': {
    'cbb64aa2-9194-472f-8e85-8bc6713be36a': ['730a11c1-b4d7-4b2d-9f30-3991b5aab241', '9fcc336a-f1d3-435f-9885-f4f27cd4ccb8'],
    'fad65d6f-2eda-46e8-ae46-826529e734f4': ['ec91eeb5-aa72-4a69-afb5-3a297cc6faa4', '2c7604ea-6267-4c21-a3d6-902fa7ab6e8b'],
    '8c28d145-1d87-47a8-a36b-e1eadd27aad7': ['6eddb8e8-5dcc-4231-a38a-b2b9a85e2535', 'e79ea6b9-1939-4344-9451-bdf0e1cf8f20', 'ba8de611-8fe3-4b89-83cd-6acbf3c51bbc'],
    '8a4fa64f-bfde-4597-be5f-f4d05fc151aa': ['a170b915-2323-4bbc-ac77-48e8f50cf2b2', '4f34cd79-1611-4763-b2d7-dbbb538d411b'],
    'bb351de3-00c5-4324-b11d-c643da294420': ['a6aef73b-31e6-4091-b347-bca95166b5b2', '77236c3b-440e-40b5-9a20-520300e7a55a'],
  },
};

// Templates reserved for the Quick Discharge / Case Note flow (PatientDetails).
// Matched case-insensitively by name; these are hidden from the normal
// appointment-driven New Visit list and never count toward the visit total.
export const QUICK_DISCHARGE_TEMPLATE_NAMES = ['case note', 'quick discharge', 'no-visit discharge'];
// Optional org template offered on the quick discharge path for Medicare /
// Medicare Advantage cases in place of the full discharge summaries. Hidden
// from the normal New Visit list and from the non-Medicare quick path.
export const MEDICARE_QUICK_DISCHARGE_TEMPLATE_NAME = 'medicare quick discharge';

export function CreateVisitModal({
  isOpen,
  onClose,
  patient,
  appointment = null,
  caseId = null,
  existingEncounterId = null,
  checkInOnCreate = false,
  onVisitCreated,
  templateNameFilter = null,
}) {
  const { user } = useUser();
  const { can } = usePermissions();
  const isRehab = isRehabSpecialty(user?.clinical_specialty);
  const usesCases = usesCasesForSpecialty(user?.organization?.settings, user?.clinical_specialty);
  // Quick discharge / case note path (templateNameFilter) stays editable for everyone —
  // backdating a discharge date is a common legitimate need.
  const canEditVisitDate = !!templateNameFilter || can('clinical_edit_visit_date');

  // Step: 'form' | 'regionPicker' | 'creating'
  const [step, setStep] = useState('form');
  // Medicare quick discharge path: null until the picker reports whether the org
  // has a "Medicare Quick Discharge" template
  const [medicareQuickFound, setMedicareQuickFound] = useState(null);

  // Form data
  const [selectedCaseId, setSelectedCaseId] = useState(caseId);
  const [selectedAppointment, setSelectedAppointment] = useState(appointment);
  // Same-day appointment found for patient + provider + date when nothing is explicitly linked (WS-D)
  const [autoLinkedAppointment, setAutoLinkedAppointment] = useState(null);
  const [dismissedAutoLinkId, setDismissedAutoLinkId] = useState(null);

  // Resolve provider for supervised users — reacts to appointment selection changes
  const { resolvedProviderId, resolving, resolutionSource, availableSupervisors, selectSupervisor } = useProviderResolution({
    patient,
    appointment: selectedAppointment,
  });
  const [encounterDate, setEncounterDate] = useState('');
  const [encounterTimeSource, setEncounterTimeSource] = useState(null);
  const [encounterType, setEncounterType] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [presentingProblems, setPresentingProblems] = useState([]);
  const [newProblem, setNewProblem] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [telehealthPatientState, setTelehealthPatientState] = useState('');
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);

  // Template selection (inline — user clicks to highlight, "Create Visit" to confirm)
  const [selectedTemplate, setSelectedTemplate] = useState(undefined); // undefined = none chosen, null = SOAP

  // Data for dropdowns
  const [patientCases, setPatientCases] = useState([]);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Case constraints
  const [caseNoteConstraints, setCaseNoteConstraints] = useState({});
  const [constraintsLoading, setConstraintsLoading] = useState(false);

  // Billing rule alerts for selected case
  const [caseEncountersForAlerts, setCaseEncountersForAlerts] = useState([]);

  // Region picker state (combined body regions + functional areas)
  const [preSelectedRegions, setPreSelectedRegions] = useState([]);
  const [preSelectedFunctionalAreas, setPreSelectedFunctionalAreas] = useState([]);
  const [regionPickerICD10, setRegionPickerICD10] = useState([]);

  // Creation
  const [isCreating, setIsCreating] = useState(false);
  // In-flight guard for the Create Visit click. State alone is not enough:
  // handleCreateVisit awaits region lookups before createVisit hides the
  // form, and every extra click in that window started its own POST /visit
  // (duplicate encounter + note pairs, 2026-10-14). A ref rejects re-entry
  // within the same render tick.
  const createInFlightRef = useRef(false);
  const [error, setError] = useState(null);
  const [noApptWarningDismissed, setNoApptWarningDismissed] = useState(false);

  // Credentialing handoff — some callers pass a stub patient without insurance,
  // so resolve the full record for the deny check
  const [fullPatient, setFullPatient] = useState(null);
  const [assignedSignerId, setAssignedSignerId] = useState(null);
  const [assignDefaultSupervisorId, setAssignDefaultSupervisorId] = useState(null);
  const { deny: signerDeny, payerName: denyPayerName, payerGroupId: denyPayerGroupId } =
    useCredentialingDeny(resolutionSource === 'self' ? fullPatient : null, user?.id);

  useEffect(() => {
    if (!signerDeny || !user?.id) return;
    let cancelled = false;
    apiService.getSupervisionRelationships({ supervisee_id: user.id, is_active: true })
      .then(rels => {
        if (!cancelled && rels?.length > 0) setAssignDefaultSupervisorId(rels[0].supervisor_id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [signerDeny, user?.id]);

  // Load dropdown data when modal opens
  useEffect(() => {
    if (!isOpen || !patient) return;

    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const [casesRes, apptsRes, typesRes] = await Promise.all([
          usesCases
            ? apiService.getCases({ patient_id: patient.id, status: 'active' })
            : Promise.resolve([]),
          apiService.getAppointments({
            patient_id: patient.id,
            status: 'scheduled,confirmed,checked_in,in_progress',
            limit: 20,
          }),
          apiService.getAppointmentTypes(user?.organization?.id),
        ]);

        const loadedCases = casesRes?.items || casesRes || [];
        setPatientCases(loadedCases);

        // The dropdown lists active cases only. A passed case id outside that
        // list (e.g. a discharged case selected on the Cases tab) would render
        // as "No case" while still being sent on create, so drop it. On the
        // quick discharge / case note path, pre-select the one active case
        // when there is exactly one; with several the clinician chooses.
        if (usesCases) {
          if (caseId && !loadedCases.some(c => c.id === Number(caseId))) {
            setSelectedCaseId(null);
          } else if (!caseId && templateNameFilter && loadedCases.length === 1) {
            setSelectedCaseId(loadedCases[0].id);
          }
        }
        setPatientAppointments(apptsRes?.items || apptsRes || []);
        setAppointmentTypes(typesRes?.items || typesRes || []);

        // Resolve the full patient (with insurance) for the credentialing check
        if (patient.insurance !== undefined) {
          setFullPatient(patient);
        } else {
          apiService.getPatient(patient.id).then(setFullPatient).catch(() => setFullPatient(patient));
        }

      } catch (err) {
        console.error('Error loading visit modal data:', err);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [isOpen, patient?.id]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setSelectedCaseId(caseId);
      setSelectedAppointment(appointment);
      setSelectedTemplate(undefined);
      setChiefComplaint('');
      setReasonForVisit('');
      setPresentingProblems([]);
      setInternalNotes('');
      setShowAdditionalDetails(false);
      setError(null);
      setIsCreating(false);
      createInFlightRef.current = false;
      setNoApptWarningDismissed(false);
      setPreSelectedRegions([]);
      setCaseNoteConstraints({});
      setAssignedSignerId(null);
      setAutoLinkedAppointment(null);
      setDismissedAutoLinkId(null);

      if (appointment?.start_timestamp) {
        setEncounterDate(timestampToDateOnly(appointment.start_timestamp));
        setEncounterTimeSource(appointment.start_timestamp);
      } else {
        const now = Date.now();
        setEncounterDate(timestampToDateOnly(now));
        setEncounterTimeSource(now);
      }

      // Set encounter type from appointment
      setEncounterType(appointment?.appointment_type || '');

      // Pre-fill reason from appointment
      if (appointment?.visit_reason) {
        setReasonForVisit(appointment.visit_reason);
      }
    }
  }, [isOpen, caseId, appointment]);

  // WS-D: with no explicit appointment, look for exactly one non-terminal, unlinked
  // appointment for this patient + provider on the encounter date and pre-link it.
  useEffect(() => {
    if (!isOpen || selectedAppointment || templateNameFilter || !patient?.id || !resolvedProviderId || !encounterDate) {
      setAutoLinkedAppointment(null);
      return;
    }
    let cancelled = false;
    apiService.getAppointments({
      patient_id: patient.id,
      provider_id: resolvedProviderId,
      start_date: encounterDate,
      end_date: encounterDate,
    })
      .then(res => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : (res?.items || []);
        const matches = items.filter(a =>
          AUTO_LINK_STATUSES.includes(a.status) && !a.encounter_id && a.id !== dismissedAutoLinkId
        );
        if (matches.length === 1) {
          setAutoLinkedAppointment(prev => (prev?.id === matches[0].id ? prev : matches[0]));
        } else {
          setAutoLinkedAppointment(null);
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Same-day appointment lookup failed (non-blocking):', err);
        setAutoLinkedAppointment(null);
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedAppointment?.id, templateNameFilter, patient?.id, resolvedProviderId, encounterDate, dismissedAutoLinkId]);

  const handleUnlinkAutoAppointment = () => {
    if (autoLinkedAppointment?.id) setDismissedAutoLinkId(autoLinkedAppointment.id);
    setAutoLinkedAppointment(null);
  };

  // Compute case constraints and fetch encounters when case changes or modal reopens
  useEffect(() => {
    if (!isOpen || !selectedCaseId || !usesCases) {
      setCaseNoteConstraints({});
      setCaseEncountersForAlerts([]);
      return;
    }
    setConstraintsLoading(true);
    computeCaseNoteConstraints(selectedCaseId, apiService, user?.organization_id)
      .then(setCaseNoteConstraints)
      .finally(() => setConstraintsLoading(false));
    apiService.getEncounters({ patient_id: patient.id, case_id: selectedCaseId, limit: 100 })
      .then(res => setCaseEncountersForAlerts(res?.items || []))
      .catch(() => setCaseEncountersForAlerts([]));
  }, [selectedCaseId, isOpen]);

  const selectedCaseObj = patientCases.find(c => c.id === Number(selectedCaseId)) || null;

  // Quick discharge path on a case billed to Medicare / Medicare Advantage:
  // the quick discharge templates are swapped for the regular discharge summary
  // templates. Coverage follows the case (no case / inherit = patient primary).
  const coveragePlanType = normalizePlanType(
    resolveCaseBillingCoverage(selectedCaseObj, fullPatient || patient).insurance?.plan_type
  );
  const medicareFullDischarge = !!templateNameFilter
    && (coveragePlanType === 'medicare' || coveragePlanType === 'medicare_advantage');

  // The offered templates change with the case's coverage; drop a stale pick
  useEffect(() => {
    if (templateNameFilter) setSelectedTemplate(undefined);
  }, [medicareFullDischarge]);

  const { violations: modalBillingViolations, pocExpired: modalPocExpired } = useCaseBillingAlerts({
    selectedCase: selectedCaseObj,
    encounters: caseEncountersForAlerts,
    patient,
    organizationId: user?.organization_id,
  });
  // Same predicate as the calendar's PN-due shading (utils/pnDue.js)
  const { pnDue } = derivePnDue({
    violations: modalBillingViolations,
    pocExpired: modalPocExpired,
    billingMode: user?.organization?.settings?.billing_mode || 'cash',
  });

  // Only hard_block violations restrict the picker; acknowledge_required /
  // soft_warning / info keep the badge + banner without narrowing note types.
  const billingRequiredNoteTypes = (() => {
    if (!modalBillingViolations.length) return null;
    const noteTypes = new Set();
    for (const v of modalBillingViolations) {
      if (v.enforcement_level !== 'hard_block') continue;
      const mapped = REQUIRED_ACTION_TO_NOTE_TYPES[v.required_action];
      if (mapped) mapped.forEach(nt => noteTypes.add(nt));
    }
    return noteTypes.size > 0 ? [...noteTypes] : null;
  })();

  // Merge designation-based note type restrictions with case constraints
  // getBlockedNoteTypes returns FULLY blocked types (aides/techs only)
  // getDraftOnlyNoteTypes returns types PTAs can create as drafts but not sign
  const userCanCreateNotes = canCreateNotes(user?.provider_designation);
  const draftOnlyNoteTypes = getDraftOnlyNoteTypes(user?.provider_designation);
  const mergedNoteConstraints = (() => {
    const merged = { ...caseNoteConstraints };
    const blocked = getBlockedNoteTypes(user?.provider_designation);
    blocked.forEach(nt => {
      if (!merged[nt]) {
        merged[nt] = `${user?.provider_designation} providers cannot create ${nt.replace(/_/g, ' ')} notes`;
      }
    });
    // Draft-only types are NOT added to constraints — they remain selectable
    return merged;
  })();

  // Org-forced note types (OR: any of the selected types) when the case is
  // PN-due, at any enforcement level and on the POC-expired path too, matching
  // the calendar shading. Types disabled by case constraints (e.g. no initial
  // evaluation yet) are dropped from the allowlist; if none remain, forcing is
  // skipped so the picker is never empty and the legacy hard_block mapping
  // applies as before.
  const configuredForcedTypes = user?.organization?.settings?.documentation?.pn_due_forced_note_types;
  const forcedNoteTypes = (Array.isArray(configuredForcedTypes) ? configuredForcedTypes : [])
    .filter(nt => !mergedNoteConstraints[nt]);
  const pnDueForcingActive = !!(pnDue && forcedNoteTypes.length > 0);
  const effectiveAllowedNoteTypes = pnDueForcingActive ? forcedNoteTypes : billingRequiredNoteTypes;

  const allowedTemplateIds = (() => {
    const orgMap = TEMPLATE_RESTRICTIONS_BY_ORG[user?.organization_id];
    if (!orgMap) return null;
    const apptTypeId = selectedAppointment?.appointment_type_id || selectedAppointment?._appointment_type?.id;
    return orgMap[apptTypeId] || null;
  })();

  const handleTemplateHighlighted = (template) => {
    // template is null for Standard SOAP, or the template object
    setSelectedTemplate(template);
  };

  const handleCreateVisit = async () => {
    if (selectedTemplate === undefined) return; // No template chosen yet
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;

    const template = selectedTemplate; // null = SOAP, or template object

    if (template && (template.uses_body_regions || template.uses_functional_areas)) {
      const { regions, areas } = await computeRegionPreselections(template);
      // An existing selection (case, or a prior same-case encounter) is used
      // as-is. The picker only appears when a dimension the template uses has
      // nothing to seed from, so the provider confirms the ICD-10 inference once.
      const bodyRegionsResolved = !template.uses_body_regions || regions.length > 0;
      const functionalAreasResolved = !template.uses_functional_areas || areas.length > 0;
      if (bodyRegionsResolved && functionalAreasResolved) {
        await createVisit(template, regions, areas);
      } else {
        createInFlightRef.current = false;
        setStep('regionPicker');
      }
    } else {
      await createVisit(template);
    }
  };

  const computeRegionPreselections = async (template) => {
    const selectedCase = patientCases.find(c => c.id === selectedCaseId);
    let regions = [];
    let areas = [];

    // Body regions: case → same-case encounters → ICD-10 inference (via picker)
    if (template.uses_body_regions) {
      if (selectedCase?.body_regions?.length > 0) {
        regions = selectedCase.body_regions;
      } else if (selectedCaseId) {
        try {
          const recentEncounters = await apiService.getEncounters({
            patient_id: patient.id,
            case_id: selectedCaseId,
            limit: 5,
          });
          const items = recentEncounters?.items || [];
          const withRegions = items.find(e => e.body_regions?.length > 0);
          if (withRegions) regions = withRegions.body_regions;
        } catch (err) {
          console.error('Error fetching case encounters for body regions:', err);
        }
      }
    }

    // Functional areas: case → same-case encounters → ICD-10 inference (via picker)
    if (template.uses_functional_areas) {
      if (selectedCase?.functional_areas?.length > 0) {
        areas = selectedCase.functional_areas;
      } else if (selectedCaseId) {
        try {
          const recentEncounters = await apiService.getEncounters({
            patient_id: patient.id,
            case_id: selectedCaseId,
            limit: 5,
          });
          const items = recentEncounters?.items || [];
          const withAreas = items.find(e => e.functional_areas?.length > 0);
          if (withAreas) areas = withAreas.functional_areas;
        } catch (err) {
          console.error('Error fetching case encounters for functional areas:', err);
        }
      }
    }

    setPreSelectedRegions(regions);
    setPreSelectedFunctionalAreas(areas);
    setRegionPickerICD10(selectedCase?.icd10_codes || []);
    return { regions, areas };
  };

  const handleRegionPickerConfirmed = async ({ bodyRegions, functionalAreas }) => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    await createVisit(selectedTemplate, bodyRegions, functionalAreas);
  };

  const handleAddProblem = () => {
    if (newProblem.trim()) {
      setPresentingProblems(prev => [...prev, newProblem.trim()]);
      setNewProblem('');
    }
  };

  const handleRemoveProblem = (index) => {
    setPresentingProblems(prev => prev.filter((_, i) => i !== index));
  };

  const fetchICDCodes = async () => {
    // Case-linked: the case is authoritative — seed from case codes or start empty
    if (selectedCaseId) {
      try {
        const caseData = await apiService.getCase(selectedCaseId);
        if (caseData?.icd10_codes?.length > 0) {
          return caseData.icd10_codes;
        }
      } catch (err) {
        console.error('Error fetching case ICD-10 codes:', err);
      }
      return [];
    }
    // No case: carry forward only from the current provider's own last signed note
    try {
      const notesRes = await apiService.getClinicalNotes({
        patient_id: patient.id,
        provider_id: user.id,
        signed: true,
        limit: 1,
      });
      if (notesRes?.items?.[0]?.icd10_codes?.length > 0) {
        return notesRes.items[0].icd10_codes;
      }
    } catch (err) {
      console.error('Error fetching previous note ICD-10 codes:', err);
    }
    return [];
  };

  const createVisit = async (template, bodyRegions = [], functionalAreas = []) => {
    setStep('creating');
    setIsCreating(true);
    setError(null);

    try {
      let encounterTimestamp;
      if (encounterDate) {
        const timeRef = new Date(encounterTimeSource || Date.now());
        const [year, month, day] = encounterDate.split('-').map(Number);
        const combined = new Date(timeRef);
        combined.setFullYear(year, month - 1, day);
        encounterTimestamp = combined.getTime();
      } else {
        encounterTimestamp = Date.now();
      }

      // Check in the patient if requested (only on commit, not on modal open)
      if (checkInOnCreate && appointment?.id) {
        try {
          await apiService.updateAppointment(appointment.id, { status: 'checked_in', checked_in_at: Date.now() });
        } catch (err) {
          console.error('Check-in failed (non-blocking):', err);
        }
      }

      // Fetch ICD-10 codes from case or previous notes
      const icd10Codes = await fetchICDCodes();

      // Fetch full template and build sections_data (matching EncounterDetail pattern)
      let sectionsData = undefined;
      if (template?.id) {
        try {
          const fullTemplate = resolveTemplateDraft(
            await apiService.getTemplate(template.id),
            isTestPatient(patient?.id, user)
          );
          if (fullTemplate?.sections?.length > 0) {
            sectionsData = {
              templateId: fullTemplate.id,
              templateVersion: fullTemplate.version || 1,
              sections: fullTemplate.sections.map(section => ({
                sectionId: section.id,
                sectionName: section.name,
                fields: (section.fields || []).map(field => ({
                  fieldId: field.id,
                  value: field.type === 'checkbox' ? false
                    : (field.type === 'checkbox_group' || field.type === 'data_grid') ? []
                    : ''
                }))
              }))
            };
          }
        } catch (err) {
          console.error('Failed to fetch full template for sections_data:', err);
        }
      }

      if (existingEncounterId) {
        const noteData = {
          encounter_id: existingEncounterId,
          patient_id: patient.id,
          provider_id: resolvedProviderId,
          treating_provider_id: resolutionSource !== 'self' ? user.id : undefined,
          assigned_signer_id: (resolutionSource === 'self' && assignedSignerId) ? assignedSignerId : undefined,
          case_id: selectedCaseId || null,
          note_type: template?.note_type || 'soap',
          template_id: template?.id || null,
          body_regions: bodyRegions,
          functional_areas: functionalAreas.length > 0 ? functionalAreas : undefined,
          icd10_codes: icd10Codes.length > 0 ? icd10Codes : undefined,
          sections_data: sectionsData || undefined,
          signed: false,
        };

        const clinicalNote = await apiService.createClinicalNote(noteData);

        // Only set clinical_note_id if encounter doesn't have one yet (first note)
        const existingEnc = await apiService.getEncounter(existingEncounterId);
        const updatePayload = {
          documentation_status: 'in_progress',
          body_regions: bodyRegions.length > 0 ? bodyRegions : undefined,
          functional_areas: functionalAreas.length > 0 ? functionalAreas : undefined,
          ...(resolutionSource !== 'self' ? { supervising_provider_id: resolvedProviderId } : {}),
        };
        if (!existingEnc.clinical_note_id) {
          updatePayload.clinical_note_id = clinicalNote.id;
        }
        await apiService.updateEncounter(existingEncounterId, updatePayload);

        // Use merge endpoint for billing codes (supports multi-note)
        if (icd10Codes.length > 0) {
          await apiService.mergeBillingCodes(existingEncounterId, {
            note_id: clinicalNote.id,
            provider_id: resolvedProviderId,
            icd10_codes: icd10Codes,
            cpt_codes: []
          });
        }

        const updatedEnc = await apiService.getEncounter(existingEncounterId);
        onVisitCreated({ encounter: updatedEnc, clinicalNote });
      } else {
        const visitData = {
          patient_id: patient.id,
          provider_id: resolvedProviderId,
          organization_id: user.organization?.id,
          encounter_date: encounterTimestamp,
          encounter_type: encounterType || undefined,
          visit_type: selectedAppointment?.is_telehealth ? 'telehealth' : 'office',
          chief_complaint: chiefComplaint || undefined,
          reason_for_visit: reasonForVisit || undefined,
          presenting_problems: presentingProblems.length > 0 ? presentingProblems : undefined,
          appointment_id: selectedAppointment?.id || autoLinkedAppointment?.id || undefined,
          duration_minutes: selectedAppointment?.duration_minutes || 30,
          internal_notes: internalNotes || undefined,
          is_telehealth: selectedAppointment?.is_telehealth || false,
          case_id: selectedCaseId || undefined,
          clinical_specialty: user?.clinical_specialty || undefined,
          appointment_type_id: selectedAppointment?.appointment_type_id || undefined,
          location_id: selectedAppointment?.location_id || user?.location_id || undefined,
          note_type: template?.note_type || 'soap',
          template_id: template?.id || undefined,
          body_regions: bodyRegions.length > 0 ? bodyRegions : undefined,
          functional_areas: functionalAreas.length > 0 ? functionalAreas : undefined,
          // Supervision field mapping (see SUPERVISION_SYSTEM.md):
          // provider_id (above) = supervisor → stored as clinical_note.provider_id
          // treating_provider_id = assistant → stored as clinical_note.treating_provider_id
          // supervising_provider_id = supervisor → stored as encounter.supervising_provider_id
          // Backend sets encounter.provider_id = treating_provider_id when supervised.
          treating_provider_id: resolutionSource !== 'self' ? user.id : undefined,
          supervising_provider_id: resolutionSource !== 'self' ? resolvedProviderId : undefined,
          assigned_signer_id: (resolutionSource === 'self' && assignedSignerId) ? assignedSignerId : undefined,
          patient_state: selectedAppointment?.is_telehealth
            ? (telehealthPatientState || undefined)
            : (user?.location?.address?.state || undefined),
          icd10_codes: icd10Codes.length > 0 ? icd10Codes : undefined,
          sections_data: sectionsData || undefined,
          counts_toward_visit_total: (() => {
            const name = template?.name?.toLowerCase();
            // Everything created from the quick discharge / case note path is documentation-only
            if (templateNameFilter || QUICK_DISCHARGE_TEMPLATE_NAMES.includes(name) || name === MEDICARE_QUICK_DISCHARGE_TEMPLATE_NAME) return false;
            return undefined;
          })(),
        };

        const result = await apiService.createVisit(visitData);

        // WS-B: the linked appointment's case follows the visit's case
        const linkedAppointment = selectedAppointment || autoLinkedAppointment;
        if (linkedAppointment && selectedCaseId && linkedAppointment.case_id !== selectedCaseId) {
          try {
            await apiService.updateAppointment(linkedAppointment.id, { case_id: selectedCaseId });
          } catch (err) {
            console.error('Appointment case_id sync failed (non-blocking):', err);
          }
        }

        await linkInvoice(selectedAppointment, result.encounter.id);

        onVisitCreated({
          encounter: result.encounter,
          clinicalNote: result.clinical_note,
        });
      }
    } catch (err) {
      console.error('Error creating visit:', err);
      setError('Failed to create visit. Please try again.');
      setStep('form');
      setIsCreating(false);
      createInFlightRef.current = false;
    }
  };

  const handleClose = () => {
    if (isCreating) return;
    onClose();
  };

  if (!isOpen) return null;

  const hasTemplateSelected = selectedTemplate !== undefined;

  const todayStr = new Date().toISOString().split('T')[0];
  const hasTodayAppointment = patientAppointments.some(a => a.appointment_date === todayStr);
  const showNoApptWarning = !appointment && !isLoadingData && !hasTodayAppointment && !noApptWarningDismissed;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {step === 'regionPicker' ? 'Select Regions' : step === 'creating' ? 'Creating Visit...' : templateNameFilter ? 'Quick Discharge / Case Note' : 'New Visit'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {patient?.first_name} {patient?.last_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isCreating}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {showNoApptWarning && !templateNameFilter && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium">No appointments scheduled today</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  This patient has no appointments for today. You can still create an encounter, or schedule an appointment first.
                </p>
              </div>
              <button onClick={() => setNoApptWarningDismissed(true)} className="p-0.5 hover:bg-amber-100 rounded">
                <X className="h-3.5 w-3.5 text-amber-400" />
              </button>
            </div>
          )}

          {modalBillingViolations.length > 0 && (
            <div className="space-y-2">
              {modalBillingViolations.map((v, idx) => {
                const isHigh = v.enforcement_level === 'hard_block' || v.enforcement_level === 'acknowledge_required';
                return (
                  <div
                    key={v.rule_id || idx}
                    className={cn(
                      "flex items-start gap-2 p-3 rounded-lg",
                      isHigh
                        ? "bg-red-50 border border-red-200"
                        : "bg-amber-50 border border-amber-200"
                    )}
                  >
                    <AlertTriangle className={cn(
                      "h-4 w-4 mt-0.5 flex-shrink-0",
                      isHigh ? "text-red-600" : "text-amber-600"
                    )} />
                    <div className="flex-1">
                      <p className={cn(
                        "text-sm font-medium",
                        isHigh ? "text-red-800" : "text-amber-800"
                      )}>
                        {v.message}
                      </p>
                      {v.resolution_guidance && (
                        <p className={cn(
                          "text-xs mt-0.5",
                          isHigh ? "text-red-600" : "text-amber-600"
                        )}>
                          {v.resolution_guidance}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!userCanCreateNotes && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700 font-medium">
                Your designation ({user?.provider_designation}) does not allow creating clinical notes.
                Clinical documentation must be completed by a licensed provider.
              </p>
            </div>
          )}

          {step === 'creating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="mt-3 text-sm text-gray-600">Creating encounter and note...</p>
            </div>
          )}

          {step === 'form' && (
            <>
              {isLoadingData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Case selector */}
                  {usesCases && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Case</label>
                      <select
                        value={selectedCaseId || ''}
                        onChange={(e) => setSelectedCaseId(e.target.value ? Number(e.target.value) : null)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">No case</option>
                        {patientCases.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name} — {c.status}
                            </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Appointment link — hidden in quick discharge path */}
                  {!templateNameFilter && <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Link2 className="h-3.5 w-3.5 inline mr-1" />
                      Link to Appointment
                    </label>
                    <select
                      value={selectedAppointment?.id || ''}
                      onChange={(e) => {
                        const apt = patientAppointments.find(a => a.id === e.target.value);
                        setSelectedAppointment(apt || null);
                        if (apt) {
                          if (apt.start_timestamp) {
                            setEncounterDate(timestampToDateOnly(apt.start_timestamp));
                            setEncounterTimeSource(apt.start_timestamp);
                          }
                          if (apt.appointment_type) setEncounterType(apt.appointment_type);
                          if (apt.visit_reason) setReasonForVisit(apt.visit_reason);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No appointment linked</option>
                      {patientAppointments.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.appointment_date} {a.start_time} — {a.appointment_type || 'Appointment'}
                        </option>
                      ))}
                    </select>
                  </div>}

                  {/* Telehealth patient state */}
                  {selectedAppointment?.is_telehealth && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Patient's Physical Location (State)
                      </label>
                      <select
                        value={telehealthPatientState}
                        onChange={(e) => setTelehealthPatientState(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select state...</option>
                        {['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
                          'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
                          'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
                          'VT','VA','WA','WV','WI','WY'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Required for telehealth — determines applicable state license and supervision rules.
                      </p>
                    </div>
                  )}

                  {/* Encounter date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="h-3.5 w-3.5 inline mr-1" />
                      {templateNameFilter ? 'Date' : 'Visit Date'}
                    </label>
                    {canEditVisitDate ? (
                      <input
                        type="date"
                        value={encounterDate}
                        onChange={(e) => setEncounterDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                        {encounterDate
                          ? (() => {
                              const [year, month, day] = encounterDate.split('-').map(Number);
                              return new Date(year, month - 1, day).toLocaleDateString();
                            })()
                          : 'Not set'}
                      </p>
                    )}
                  </div>

                  {/* Physician / non-rehab additional details — hidden in quick discharge path */}
                  {!isRehab && !templateNameFilter && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        {showAdditionalDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        Additional Details
                      </button>

                      {showAdditionalDetails && (
                        <div className="space-y-3 pl-2 border-l-2 border-gray-100">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Encounter Type</label>
                            <select
                              value={encounterType}
                              onChange={(e) => setEncounterType(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select type...</option>
                              {appointmentTypes.filter(t => t.is_active !== false).map(t => (
                                <option key={t.id} value={t.name}>{t.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Chief Complaint</label>
                            <input
                              type="text"
                              value={chiefComplaint}
                              onChange={(e) => setChiefComplaint(e.target.value)}
                              placeholder="Primary reason for visit"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Visit</label>
                            <textarea
                              value={reasonForVisit}
                              onChange={(e) => setReasonForVisit(e.target.value)}
                              placeholder="Detailed reason"
                              rows={2}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Presenting Problems</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newProblem}
                                onChange={(e) => setNewProblem(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProblem())}
                                placeholder="Add problem"
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <Button variant="outline" size="sm" onClick={handleAddProblem}>Add</Button>
                            </div>
                            {presentingProblems.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {presentingProblems.map((p, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                    {p}
                                    <button onClick={() => handleRemoveProblem(i)} className="hover:text-red-500">
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                            <textarea
                              value={internalNotes}
                              onChange={(e) => setInternalNotes(e.target.value)}
                              placeholder="Not visible to patient"
                              rows={2}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Inline template selection */}
                  <div className="pt-2 border-t border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note Template</label>
                    {pnDueForcingActive && !templateNameFilter && (
                      <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-800">
                          Progress note due for this case. Note type limited to {forcedNoteTypes.map(nt => FORCED_NOTE_TYPE_LABELS[nt] || nt).join(' or ')}.
                        </p>
                      </div>
                    )}
                    {medicareFullDischarge && medicareQuickFound === false && (
                      <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-800">
                          Medicare case: a full Discharge Summary is required. Quick discharge templates are not available.
                        </p>
                      </div>
                    )}
                    <NoteTemplateSelector
                      isOpen={true}
                      inline={true}
                      onSelect={handleTemplateHighlighted}
                      onCancel={() => {}}
                      encounterType={encounterType}
                      organizationId={user?.organization?.id}
                      userId={user?.id}
                      userSpecialty={user?.clinical_specialty}
                      disabledNoteTypes={mergedNoteConstraints}
                      selectedTemplateId={selectedTemplate === undefined ? '__none__' : (selectedTemplate?.id || null)}
                      allowedTemplateIds={allowedTemplateIds}
                      templateNameFilter={medicareFullDischarge ? ['case note', MEDICARE_QUICK_DISCHARGE_TEMPLATE_NAME] : templateNameFilter}
                      includeNoteTypes={medicareFullDischarge ? ['discharge_summary'] : null}
                      excludeTemplateNames={medicareFullDischarge
                        ? ['quick discharge', 'no-visit discharge']
                        : (templateNameFilter ? null : [...QUICK_DISCHARGE_TEMPLATE_NAMES, MEDICARE_QUICK_DISCHARGE_TEMPLATE_NAME])}
                      preferredTemplateNames={medicareFullDischarge ? [MEDICARE_QUICK_DISCHARGE_TEMPLATE_NAME] : null}
                      onPreferredMatched={medicareFullDischarge ? setMedicareQuickFound : null}
                      allowedNoteTypes={templateNameFilter ? null : effectiveAllowedNoteTypes}
                      ignoreSpecialty={!!templateNameFilter && !medicareFullDischarge}
                    />
                    {draftOnlyNoteTypes.length > 0 && selectedTemplate?.note_type && draftOnlyNoteTypes.includes(selectedTemplate.note_type) && (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          This note type will be created as a draft. Your supervising provider must review and sign it.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {step === 'regionPicker' && (
            <ClinicalRegionPicker
              isOpen={true}
              onConfirm={handleRegionPickerConfirmed}
              onCancel={() => setStep('form')}
              icd10Codes={regionPickerICD10}
              initialBodyRegions={preSelectedRegions}
              initialFunctionalAreas={preSelectedFunctionalAreas}
              showBodyRegions={!!selectedTemplate?.uses_body_regions}
              showFunctionalAreas={!!selectedTemplate?.uses_functional_areas}
              source={preSelectedRegions.length > 0 || preSelectedFunctionalAreas.length > 0 ? 'case' : 'icd10'}
            />
          )}
        </div>

        {/* Footer */}
        {step === 'form' && (
          <div className="px-6 py-4 border-t border-gray-200">
            {signerDeny && resolutionSource === 'self' && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 font-medium">
                      You aren&apos;t credentialed with {denyPayerName || "this patient's payer"}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5 mb-2">
                      Assign a credentialed provider to sign this visit. It will be billed under their name.
                      You can also assign later from the note.
                    </p>
                    <AssignedSignerSelect
                      value={assignedSignerId}
                      onChange={setAssignedSignerId}
                      locationId={selectedAppointment?.location_id || user?.location_id}
                      payerGroupId={denyPayerGroupId}
                      defaultSupervisorId={assignDefaultSupervisorId}
                      clinicalSpecialty={user?.clinical_specialty}
                    />
                  </div>
                </div>
              </div>
            )}
            {resolutionSource === 'supervisor' && availableSupervisors.length > 1 && !resolving && (
              <div className="mb-2">
                <label className="text-xs font-medium text-gray-500 block mb-1">Supervising Provider</label>
                <select
                  value={resolvedProviderId}
                  onChange={(e) => selectSupervisor(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableSupervisors.map(rel => (
                    <option key={rel.supervisor_id} value={rel.supervisor_id}>
                      {rel.supervisor_provider?.first_name} {rel.supervisor_provider?.last_name}
                      {rel.supervisor_provider?.provider_designation ? ` (${rel.supervisor_provider.provider_designation})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {resolutionSource !== 'self' && !(resolutionSource === 'supervisor' && availableSupervisors.length > 1) && !resolving && (
              <p className="text-xs text-gray-500 mb-2">
                Provider: {resolutionSource === 'appointment' ? 'From appointment' : 'From supervisor'}
              </p>
            )}
            {!selectedAppointment && autoLinkedAppointment && (
              <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                <Link2 className="h-3.5 w-3.5 text-gray-400" />
                <span>
                  Linked to {encounterDate === todayStr ? "today's" : `the ${encounterDate}`} {formatAppointmentTime(autoLinkedAppointment)} appointment
                </span>
                <button type="button" onClick={handleUnlinkAutoAppointment} className="text-primary hover:underline ml-1">
                  Unlink
                </button>
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateVisit}
                disabled={isLoadingData || !hasTemplateSelected || resolving || constraintsLoading || isCreating}
              >
                {resolving ? 'Resolving provider...' : 'Create Visit'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
